/* Sofia Lark hero: collection index, slow crossfade, and a one-time GSAP intro.
   - The page is complete without this file: the first photograph, text and button render in HTML.
   - Autoplay gives every photograph the same time: it starts once the intro has revealed the first
     one, and advances when the progress bar under the current name finishes. It pauses with the
     button, while keyboard focus is inside the hero, when it is off screen and when the tab is
     hidden, and never starts if the visitor prefers reduced motion.
   - The intro loads GSAP only when motion is allowed. If it cannot load, a timer set in the
     section reveals everything anyway. */
(function () {
  'use strict';

  var GSAP_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function splitTitle(title) {
    if (!title || title.dataset.split) return [];
    var text = title.textContent.trim();
    title.setAttribute('aria-label', text);
    title.dataset.split = 'true';
    title.textContent = '';
    var inners = [];
    text.split(/\s+/).forEach(function (word, i) {
      if (i > 0) title.appendChild(document.createTextNode(' '));
      var outer = document.createElement('span');
      outer.className = 'sl-word';
      outer.setAttribute('aria-hidden', 'true');
      var inner = document.createElement('span');
      inner.className = 'sl-word__inner';
      inner.textContent = word;
      outer.appendChild(inner);
      title.appendChild(outer);
      inners.push(inner);
    });
    return inners;
  }

  function loadGsap(callback) {
    if (window.gsap) { callback(window.gsap); return; }
    var existing = document.querySelector('script[data-sl-gsap]');
    var script = existing || document.createElement('script');
    script.addEventListener('load', function () { if (window.gsap) callback(window.gsap); });
    if (!existing) {
      script.src = GSAP_SRC;
      script.async = true;
      script.setAttribute('data-sl-gsap', '');
      document.head.appendChild(script);
    }
  }

  function intro(hero, done) {
    if (reduceMotion || !hero.classList.contains('sl-hero--pre')) { done(); return; }
    // If GSAP never arrives, the section's own timer reveals the hero; start with it.
    var fallback = setTimeout(done, 2700);
    var title = hero.querySelector('[data-sl-hero-title]');
    var media = hero.querySelector('[data-sl-hero-media]');
    var fades = hero.querySelectorAll('[data-sl-hero-fade]');

    loadGsap(function (gsap) {
      if (!hero.classList.contains('sl-hero--pre')) return; // fallback already revealed it
      var words = splitTitle(title);
      gsap.set(words, { yPercent: 110 });
      gsap.set(fades, { opacity: 0, y: 18 });
      gsap.set(media, { clipPath: 'inset(0% 0% 0% 100%)' });
      hero.classList.remove('sl-hero--pre');

      gsap.timeline({ defaults: { ease: 'power3.out' } })
        .to(media, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.3, ease: 'power3.inOut' }, 0)
        .to(words, { yPercent: 0, duration: 1.05, stagger: 0.08 }, 0.35)
        .to(fades, { opacity: 1, y: 0, duration: 0.8, stagger: 0.1, clearProps: 'transform' }, 0.85)
        .set(media, { clearProps: 'clipPath' }, 1.3)
        .call(function () { clearTimeout(fallback); done(); }, null, 1.3);
    });
  }

  function init(hero) {
    if (hero.dataset.ready) return;
    hero.dataset.ready = 'true';

    var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-sl-hero-slide]'));
    var choices = Array.prototype.slice.call(hero.querySelectorAll('[data-sl-hero-choice]'));
    var cta = hero.querySelector('[data-sl-hero-cta]');
    var ctaLabel = hero.querySelector('[data-sl-hero-cta-label]');
    var pauseBtn = hero.querySelector('[data-sl-hero-pause]');
    var interval = parseInt(hero.dataset.interval, 10) || 5000;
    var canPlay = hero.dataset.autoplay === 'true' && !reduceMotion && slides.length > 1;

    var index = 0;
    var started = false;
    var userPaused = false;
    var holds = { focus: false, offscreen: false, hidden: false };

    hero.style.setProperty('--sl-hero-interval', interval + 'ms');

    function go(next) {
      if (!slides.length) return;
      index = (next + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        if (active) { slide.removeAttribute('aria-hidden'); } else { slide.setAttribute('aria-hidden', 'true'); }
      });
      choices.forEach(function (choice, i) {
        choice.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      var choice = choices[index];
      if (choice && cta) {
        if (choice.dataset.url) cta.setAttribute('href', choice.dataset.url);
        if (ctaLabel && choice.dataset.label) ctaLabel.textContent = choice.dataset.label;
      }
      restartProgress();
    }

    function restartProgress() {
      if (!canPlay || !started) return;
      // Re-trigger the CSS animation on the current progress bar.
      hero.removeAttribute('data-playing');
      void hero.offsetWidth;
      hero.setAttribute('data-playing', 'true');
    }

    function updatePaused() {
      if (!canPlay) return;
      var paused = userPaused || holds.focus || holds.offscreen || holds.hidden;
      hero.setAttribute('data-paused', paused ? 'true' : 'false');
    }

    choices.forEach(function (choice, i) {
      choice.addEventListener('click', function () { go(i); });
      var bar = choice.querySelector('.sl-hero__progress');
      if (bar) {
        bar.addEventListener('animationend', function () {
          if (canPlay && i === index) go(index + 1);
        });
      }
    });

    if (pauseBtn) {
      // Shown only when the photographs actually change on their own.
      pauseBtn.hidden = !canPlay;
      if (canPlay) {
        pauseBtn.addEventListener('click', function () {
          userPaused = !userPaused;
          pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
          pauseBtn.setAttribute('aria-label', userPaused ? 'Turpināt attēlu maiņu' : 'Apturēt attēlu maiņu');
          updatePaused();
        });
      }
    }

    if (canPlay) {
      hero.addEventListener('focusin', function (e) {
        // Keyboard focus holds the photograph; a mouse click on a name does not stop autoplay.
        var keyboard = true;
        try { keyboard = e.target.matches(':focus-visible'); } catch (err) { keyboard = true; }
        if (keyboard) { holds.focus = true; updatePaused(); }
      });
      hero.addEventListener('focusout', function (e) {
        if (!e.relatedTarget || !hero.contains(e.relatedTarget)) { holds.focus = false; updatePaused(); }
      });
      document.addEventListener('visibilitychange', function () { holds.hidden = document.hidden; updatePaused(); });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          holds.offscreen = !entries[0].isIntersecting;
          updatePaused();
        }, { threshold: 0.25 }).observe(hero);
      }
      updatePaused();

      // The photographs after the first are lazy; fetch them now so each crossfade lands on a loaded image.
      slides.slice(1).forEach(function (slide) {
        var img = slide.querySelector('img');
        if (img) img.loading = 'eager';
      });
    }

    intro(hero, function () {
      if (started) return;
      started = true;
      restartProgress();
    });
  }

  function boot() {
    document.querySelectorAll('[data-sl-hero]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-initialise when the section is added or re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var hero = event.target.querySelector('[data-sl-hero]');
    if (hero) init(hero);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var hero = event.target.closest('[data-sl-hero]');
    if (!hero) return;
    var slides = hero.querySelectorAll('[data-sl-hero-slide]');
    var i = Array.prototype.indexOf.call(slides, event.target);
    var choice = hero.querySelectorAll('[data-sl-hero-choice]')[i];
    if (choice) choice.click();
  });
})();
